import { ACTIVE_COLOR, COLORS } from '../../const/address.const';
import { LinkItem, TableType } from '@graph/types';
import { AddressTransactionsFilterTxType, Maybe } from '@apolloGenerated';
import { LineType } from '@rubin-dev/octavius';
import { LinkService } from './LinkService';
import getReverseTxType from '../../libs/helpers/getReverseTxType';
import {
  GraphAnnotation,
  GraphLineAmount,
  GraphLineContentWrap,
  GraphCustomColor,
  Transaction,
} from '@graph/modules';
import { Annotation } from '@graph/models/Annotation/Annotation';
import { Meta } from '@graph/models';
import { v4 as uuidv4 } from 'uuid';

export type AmountType = {
  type: AddressTransactionsFilterTxType;
  value: number;
  txid: string;
  token: string;
};
export type LinkDataParams = {
  amount?: AmountType[];
  color?: Maybe<string>;
  annotation?: Maybe<Annotation>;
  token?: Maybe<string>;
};

export class Link {
  hash: string;
  target: string;
  source: string;
  amount: AmountType[] = [];
  color?: Maybe<GraphCustomColor>;
  annotation: Annotation;
  link?: LinkItem & { token?: string; amount?: AmountType[] };
  token?: string;

  constructor(targetHash: string, sourceHash: string, params?: LinkDataParams) {
    this.hash = LinkService.generateLinkHash(sourceHash, targetHash);
    this.target = targetHash;
    this.source = sourceHash;

    if (params?.amount) this.amount = params.amount;
    if (params?.token) this.token = params.token;
    if (
      params?.color &&
      Object.values(GraphCustomColor).includes(params.color as GraphCustomColor)
    )
      this.color = params.color as GraphCustomColor;
    this.annotation =
      params?.annotation || new Annotation(uuidv4(), this.hash, TableType.Transaction);
  }

  addAmount(
    txid: string,
    value: number,
    type: AddressTransactionsFilterTxType,
    token: string,
  ) {
    this.amount = [...this.amount, { txid, value, type, token }];
  }

  private removeAmount(txid: string, type: AddressTransactionsFilterTxType) {
    this.amount = this.amount.filter((el) => !(el.txid === txid && el.type === type));
  }

  private checkActiveLink(hash: string): boolean {
    return this.source === hash || this.target === hash;
  }

  private getTotalAmount(amountItems: AmountType[]): Record<string, string | number> {
    return amountItems.reduce((acc, currentValue) => {
      acc[currentValue.token as 'token'] = String(
        currentValue.value + +(acc[currentValue.token as 'token'] || 0),
      );
      return acc;
    }, {} as AmountType);
  }

  private getAmountElement(
    amount: Record<string, number | string>,
    isActive?: boolean,
  ): JSX.Element {
    return (
      <GraphLineAmount
        amount={amount}
        active={isActive}
        token={this.link?.token}
        color={this.getLinkColor(!!isActive)}
        network={Meta.network}
      />
    );
  }

  private getAnnotationElement(
    hash: string,
    parentHash: string,
    text: string,
    color: string,
  ): JSX.Element {
    return (
      <GraphAnnotation
        hash={hash}
        parentHash={parentHash}
        type={TableType.Transaction}
        text={text}
        color={color}
      />
    );
  }

  private getAmountSlot(isActive?: boolean): Partial<LinkItem['label']> {
    if (!this.amount.length) return {};

    const sentAmount = this.amount.filter(
      (el) => el.type === AddressTransactionsFilterTxType.Sent,
    );

    const receivedAmount = this.amount.filter(
      (el) => el.type === AddressTransactionsFilterTxType.Receives,
    );

    const annotation = this.annotation.getText();
    const annotationColor = this.annotation.getColor() || this.getLinkColor(!!isActive);

    if (receivedAmount.length && sentAmount.length) {
      return {
        source: this.getAmountElement(this.getTotalAmount(receivedAmount), isActive),
        center: annotation
          ? this.getAnnotationElement(
              this.annotation.hash,
              this.hash,
              annotation,
              annotationColor,
            )
          : '',
        target: this.getAmountElement(this.getTotalAmount(sentAmount), isActive),
      };
    }

    return {
      center: (
        <GraphLineContentWrap
          amountSlot={this.getAmountElement(
            this.getTotalAmount(receivedAmount.length ? receivedAmount : sentAmount),
            isActive,
          )}
          annotationSlot={
            annotation
              ? this.getAnnotationElement(
                  this.annotation.hash,
                  this.hash,
                  annotation,
                  annotationColor,
                )
              : null
          }
        />
      ),
    };
  }

  private getDirection(): LineType['direction'] {
    const receivesLines = this.amount.filter(
      (el) => el.type === AddressTransactionsFilterTxType.Receives,
    );
    const sentLines = this.amount.filter(
      (el) => el.type === AddressTransactionsFilterTxType.Sent,
    );
    if (receivesLines.length && sentLines.length) return 'all';
    if (sentLines.length) return 'target';
    return 'source';
  }

  private getLinkColor(isActive: boolean) {
    if (this.color) return this.color;
    if (isActive) return ACTIVE_COLOR;
    return COLORS.large;
  }

  setCustomColor(color: Link['color']) {
    this.color = color;
  }

  sumLink({ txid, amount, type, targetAddress, sourceAddress, token }: Transaction) {
    let realType = type;
    const isReverse = this.target !== targetAddress || this.source !== sourceAddress;
    if (isReverse) realType = getReverseTxType(type);
    this.addAmount(txid, +amount, realType, token);
  }

  minusLink({ sourceAddress, targetAddress, type, txid }: Transaction) {
    this.removeAmount(
      txid,
      this.source === targetAddress || this.target === sourceAddress
        ? getReverseTxType(type)
        : type,
    );
  }

  setLink(currentHash: string): void {
    const isActive = this.checkActiveLink(currentHash);
    this.link = {
      source: this.source as any,
      target: this.target as any,
      color: this.getLinkColor(isActive),
      label: {
        ...this.getAmountSlot(isActive),
        currentData: {
          type: TableType.AnnotationLink,
          text: this.annotation.getText() || '',
          parentHash: `${this.source},${this.target}`,
        },
      },
      amount: this.amount,
      token: this.token || '',
      direction: this.getDirection(),
      type: TableType.Transaction,
      annotation: this.annotation.getText(),
    };
  }

  activateLink(): void {
    this.link = {
      ...this.link,
      label: {
        ...this.getAmountSlot(true),
        currentData: {
          type: TableType.AnnotationLink,
          text: this.annotation.getText() || '',
          parentHash: `${this.source},${this.target}`,
        },
      },
      color: ACTIVE_COLOR,
    } as LinkItem;
  }

  checkCanDeleted(): boolean {
    return this.amount.length === 1;
  }
}
